import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import {
    ArrowLeft,
    Award,
    ChevronDown,
    ChevronRight,
    ExternalLink,
    Eye,
    FileText,
    HelpCircle,
    Pencil,
    PlayCircle,
    Plus,
    RefreshCw,
    Settings2,
    Trash2,
    Type,
    GripVertical,
} from "lucide-react";

import { fetchModulesCleRequest } from "../../../redux/actions/modulesCleActions";
import { fetchFormationsRequest } from "../../../redux/actions/formationsActions";
import { fetchCampagnesRequest } from "../../../redux/actions/campagnesActions";
import { getAllEvaluationsRequest } from "../../../redux/actions/evaluationActions";
import { getAllQuizzesRequest } from "../../../redux/actions/quizActions";
import { fetchEventsRequest } from "../../../redux/actions/eventsActions";
import { fetchCertificatsRequest } from "../../../redux/actions/certificatsActions";
import {
    clearCurrentModuleCleContent,
    deleteModuleCleContentRequest,
    getModuleCleContentRequest,
    getModuleCleContentsRequest,
    reorderModuleCleContentsRequest,
} from "../../../redux/actions/moduleCleContentActions";
import {
    createModuleSectionRequest,
    deleteModuleSectionRequest,
    getModuleSectionsRequest,
    reorderModuleSectionsRequest,
} from "../../../redux/actions/moduleCleSectionActions";
import ModuleCleContentDetailsPanel from "../components/ModuleCleContentDetailsPanel";
import ModuleCleContentFormPanel from "../components/ModuleCleContentFormPanel";
import ModuleCleContentUnlockPanel from "../components/ModuleCleContentUnlockPanel";
import ModuleCleSectionFormPanel from "../components/ModuleCleSectionFormPanel";
import {
    getModuleCleContentTypeLabel,
    getModuleCleTypeLabel,
    getModuleSectionStatusLabel,
    reorderContentsLocally,
} from "../moduleCleContentHelpers";
import "../ModuleCleContent.css";

const reorderSectionsLocally = (list, draggedId, targetId) => {
    const draggedIndex = list.findIndex((item) => item.id === draggedId);
    const targetIndex = list.findIndex((item) => item.id === targetId);
    if (draggedIndex < 0 || targetIndex < 0 || draggedIndex === targetIndex) return list;

    const next = [...list];
    const [draggedItem] = next.splice(draggedIndex, 1);
    next.splice(targetIndex, 0, draggedItem);
    return next.map((item, index) => ({ ...item, ordre: index + 1 }));
};

export default function ModuleCleContentsManagement() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { moduleCleId } = useParams();

    const modulesCleState = useSelector((state) => state.modulesCle || {});
    const moduleCleContentState = useSelector((state) => state.moduleCleContent || {});
    const moduleCleSectionState = useSelector((state) => state.moduleCleSection || {});
    const evaluations = useSelector((state) => state.evaluations?.evaluations || []);
    const quizzes = useSelector((state) => state.quizzes?.quizzes || []);
    const events = useSelector((state) => state.events?.events || []);
    const certificats = useSelector((state) => state.certificats?.certificats || []);

    const moduleItem = useMemo(
        () => (modulesCleState.modulesCle || []).find((item) => String(item.id) === String(moduleCleId)),
        [moduleCleId, modulesCleState.modulesCle]
    );

    const reduxContents = moduleCleContentState.items || [];
    const reduxSections = moduleCleSectionState.items || [];

    const [displayContents, setDisplayContents] = useState([]);
    const [displaySections, setDisplaySections] = useState([]);
    const [draggedContentId, setDraggedContentId] = useState(null);
    const [draggedSectionId, setDraggedSectionId] = useState(null);

    const [collapsedSections, setCollapsedSections] = useState(new Set());

    const [contentPanelOpen, setContentPanelOpen] = useState(false);
    const [contentPanelMode, setContentPanelMode] = useState("create");
    const [initialSectionId, setInitialSectionId] = useState(null);

    const [unlockPanelOpen, setUnlockPanelOpen] = useState(false);
    const [unlockTarget, setUnlockTarget] = useState(null);
    const [detailPanelOpen, setDetailPanelOpen] = useState(false);
    const [detailTarget, setDetailTarget] = useState(null);

    const [sectionPanelOpen, setSectionPanelOpen] = useState(false);
    const [sectionPanelMode, setSectionPanelMode] = useState("create");
    const [sectionTarget, setSectionTarget] = useState(null);

    useEffect(() => {
        dispatch(fetchModulesCleRequest());
        dispatch(fetchFormationsRequest());
        dispatch(fetchCampagnesRequest());
        dispatch(getAllEvaluationsRequest());
        dispatch(getAllQuizzesRequest());
        dispatch(fetchEventsRequest());
        dispatch(fetchCertificatsRequest());
        dispatch(getModuleSectionsRequest(moduleCleId));
        dispatch(getModuleCleContentsRequest(moduleCleId));
    }, [dispatch, moduleCleId]);

    useEffect(() => {
        setDisplayContents([...reduxContents]);
    }, [reduxContents]);

    useEffect(() => {
        setDisplaySections([...reduxSections].sort((left, right) => (left.ordre || 0) - (right.ordre || 0)));
    }, [reduxSections]);

    const contentsBySection = useMemo(() => {
        const grouped = {};
        displayContents.forEach((content) => {
            const key = String(content.sectionId || "unassigned");
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(content);
        });
        Object.keys(grouped).forEach((key) => {
            grouped[key] = grouped[key].sort((left, right) => (left.ordre || 0) - (right.ordre || 0));
        });
        return grouped;
    }, [displayContents]);

    const toggleSection = (sectionId) => {
        const next = new Set(collapsedSections);
        if (next.has(sectionId)) next.delete(sectionId);
        else next.add(sectionId);
        setCollapsedSections(next);
    };

    const closeAllSidePanels = () => {
        setContentPanelOpen(false);
        setUnlockPanelOpen(false);
        setDetailPanelOpen(false);
        setSectionPanelOpen(false);
        setUnlockTarget(null);
        setDetailTarget(null);
        setSectionTarget(null);
        setInitialSectionId(null);
        dispatch(clearCurrentModuleCleContent());
    };

    const openCreateSection = () => {
        closeAllSidePanels();
        setSectionPanelMode("create");
        setSectionPanelOpen(true);
    };

    const openEditSection = (section) => {
        closeAllSidePanels();
        setSectionTarget(section);
        setSectionPanelMode("edit");
        setSectionPanelOpen(true);
    };

    const openCreateContent = (sectionId = null) => {
        closeAllSidePanels();
        setContentPanelMode("create");
        setInitialSectionId(sectionId);
        setContentPanelOpen(true);
    };

    const openEditContent = (contentId) => {
        closeAllSidePanels();
        setContentPanelMode("edit");
        setContentPanelOpen(true);
        dispatch(getModuleCleContentRequest(moduleCleId, contentId));
    };

    const openUnlockPanel = (content) => {
        closeAllSidePanels();
        setUnlockTarget(content);
        setUnlockPanelOpen(true);
    };

    const openDetailPanel = (content) => {
        closeAllSidePanels();
        setDetailTarget(content);
        setDetailPanelOpen(true);
    };

    const handleDeleteContent = async (contentId, title) => {
        const confirmation = await Swal.fire({
            title: "Supprimer ce contenu ?",
            text: title,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Supprimer",
            cancelButtonText: "Annuler",
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#2f4595",
        });

        if (!confirmation.isConfirmed) return;
        dispatch(deleteModuleCleContentRequest(moduleCleId, contentId));
    };

    const handleDeleteSection = async (section) => {
        const confirmation = await Swal.fire({
            title: "Supprimer cette section ?",
            text: section.title,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Supprimer",
            cancelButtonText: "Annuler",
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#2f4595",
        });

        if (!confirmation.isConfirmed) return;
        dispatch(deleteModuleSectionRequest(moduleCleId, section.id));
    };

    const handleSectionDrop = (targetSectionId) => {
        if (!draggedSectionId || draggedSectionId === targetSectionId) {
            setDraggedSectionId(null);
            return;
        }

        const previous = displaySections;
        const reordered = reorderSectionsLocally(displaySections, draggedSectionId, targetSectionId);
        setDisplaySections(reordered);
        setDraggedSectionId(null);

        dispatch(
            reorderModuleSectionsRequest(moduleCleId, reordered.map((item) => item.id), {
                onError: () => setDisplaySections(previous),
            })
        );
    };

    const handleContentDrop = (sectionId, targetContentId) => {
        if (!draggedContentId || draggedContentId === targetContentId) {
            setDraggedContentId(null);
            return;
        }

        const currentSectionList = contentsBySection[String(sectionId)] || [];
        const previous = displayContents;
        const reorderedSectionList = reorderContentsLocally(currentSectionList, draggedContentId, targetContentId);
        const remaining = displayContents.filter((item) => String(item.sectionId) !== String(sectionId));
        const merged = [...remaining, ...reorderedSectionList];
        setDisplayContents(merged);
        setDraggedContentId(null);

        dispatch(
            reorderModuleCleContentsRequest(moduleCleId, sectionId, reorderedSectionList.map((item) => item.id), {
                onError: () => setDisplayContents(previous),
            })
        );
    };

    const getContentIcon = (type) => {
        switch (type) {
            case "PDF": return <FileText size={16} />;
            case "WORD": return <FileText size={16} />;
            case "VIDEO": return <PlayCircle size={16} />;
            case "QUIZ": return <HelpCircle size={16} />;
            case "EVALUATION": return <Award size={16} />;
            case "LIEN_EXTERNE": return <ExternalLink size={16} />;
            case "ARTICLE": return <Type size={16} />;
            default: return <FileText size={16} />;
        }
    };

    return (
        <div className="mcp-page">
            <header className="mcp-header-card">
                <div className="mcp-header-card__left">
                    <button type="button" className="mcp-back-btn" onClick={() => navigate("/formation/responsable/modules")}>
                        <ArrowLeft size={16} />
                        Retour aux modules
                    </button>
                    <div className="mcp-header-card__title">
                        <h1>Gestion des Sections et Contenus</h1>
                        <p>{moduleItem?.nom || "Module"} · {moduleItem?.reference || "Sans référence"}</p>
                    </div>
                </div>

                <div className="mcp-header-card__actions">
                    <button
                        type="button"
                        className="mcp-btn mcp-btn--secondary"
                        onClick={() => {
                            dispatch(getModuleSectionsRequest(moduleCleId));
                            dispatch(getModuleCleContentsRequest(moduleCleId));
                        }}
                    >
                        <RefreshCw size={14} />
                        Rafraîchir
                    </button>
                    <button type="button" className="mcp-btn mcp-btn--secondary" onClick={openCreateSection}>
                        <Plus size={14} />
                        Ajouter section
                    </button>
                    <button
                        type="button"
                        className="mcp-btn mcp-btn--primary"
                        onClick={() => {
                            if (!displaySections.length) {
                                dispatch(createModuleSectionRequest(moduleCleId, { title: "Général" }));
                                return;
                            }
                            openCreateContent(displaySections[0]?.id || null);
                        }}
                    >
                        <Plus size={14} />
                        Nouveau contenu
                    </button>
                </div>
            </header>

            <main className={`mcp-layout ${contentPanelOpen || unlockPanelOpen || detailPanelOpen || sectionPanelOpen ? "has-panel" : ""}`}>
                <div className="mcp-main-card">
                    {!displaySections.length ? (
                        <div className="mcp-empty-state">Aucune section n'est encore rattachée à ce module.</div>
                    ) : (
                        <div className="mcp-section-list">
                            {displaySections.map((section) => {
                                const sectionContents = contentsBySection[String(section.id)] || [];
                                const isCollapsed = collapsedSections.has(section.id);

                                return (
                                    <article
                                        key={section.id}
                                        className="mcp-section-card"
                                        draggable
                                        onDragStart={() => setDraggedSectionId(section.id)}
                                        onDragOver={(event) => event.preventDefault()}
                                        onDrop={() => handleSectionDrop(section.id)}
                                    >
                                        <div className="mcp-section-card__header" onClick={() => toggleSection(section.id)}>
                                            <div className="mcp-section-card__title-area">
                                                <div className="mcp-drag-handle" onClick={(e) => e.stopPropagation()}>
                                                    <GripVertical size={18} />
                                                </div>
                                                <button className="mcp-icon-btn">
                                                    {isCollapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
                                                </button>
                                                <div className="mcp-section-card__badge">Section {section.ordre}</div>
                                                <div className="mcp-section-card__title-text">
                                                    <h3>{section.title}</h3>
                                                </div>
                                            </div>

                                            <div className="mcp-section-card__info">
                                                <div className="mcp-section-card__stats">
                                                    <span className={`mcp-stat-badge ${section.visible ? "is-visible" : "is-hidden"}`}>
                                                        {section.visible ? "Visible par tous" : "Masqué"}
                                                    </span>
                                                    {section.status !== "ACTIF" && (
                                                        <span className="mcp-stat-badge">{getModuleSectionStatusLabel(section.status)}</span>
                                                    )}
                                                    <span className="mcp-stat-badge">{sectionContents.length} contenu(s)</span>
                                                </div>
                                                <div className="mcp-content-card__actions" onClick={(e) => e.stopPropagation()}>
                                                    <button type="button" className="mcp-icon-btn" title="Ajouter contenu" onClick={() => openCreateContent(section.id)}>
                                                        <Plus size={16} />
                                                    </button>
                                                    <button type="button" className="mcp-icon-btn" title="Modifier" onClick={() => openEditSection(section)}>
                                                        <Pencil size={15} />
                                                    </button>
                                                    <button type="button" className="mcp-icon-btn mcp-icon-btn--danger" title="Supprimer" onClick={() => handleDeleteSection(section)}>
                                                        <Trash2 size={15} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {!isCollapsed && (
                                            <div className="mcp-content-table-container">
                                                {!sectionContents.length ? (
                                                    <div className="mcp-empty-inline">Aucun contenu dans cette section. Cliquez sur "+" pour en ajouter.</div>
                                                ) : (
                                                    <table className="mcp-content-table">
                                                        <thead>
                                                            <tr>
                                                                <th className="col-drag"></th>
                                                                <th className="col-num">#</th>
                                                                <th className="col-content">Contenu</th>
                                                                <th className="col-status">Statut</th>
                                                                <th className="col-conditions">Conditions</th>
                                                                <th className="col-actions">Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {sectionContents.map((content) => (
                                                                <tr
                                                                    key={content.id}
                                                                    className={draggedContentId === content.id ? "is-dragging" : ""}
                                                                    draggable
                                                                    onDragStart={() => setDraggedContentId(content.id)}
                                                                    onDragOver={(event) => event.preventDefault()}
                                                                    onDrop={() => handleContentDrop(section.id, content.id)}
                                                                >
                                                                    <td className="col-drag">
                                                                        <div className="mcp-drag-handle">
                                                                            <GripVertical size={14} />
                                                                        </div>
                                                                    </td>
                                                                    <td className="col-num">{content.ordre}</td>
                                                                    <td className="col-content">
                                                                        <div className="mcp-content-item-cell">
                                                                            <div className="mcp-content-icon">
                                                                                {getContentIcon(content.contentType)}
                                                                            </div>
                                                                            <span className="mcp-content-title">{content.title}</span>
                                                                        </div>
                                                                    </td>
                                                                    <td className="col-status">
                                                                        <span className={`mcp-badge ${content.active ? "mcp-badge--active" : "mcp-badge--inactive"}`}>
                                                                            {content.active ? "ACTIF" : "INACTIF"}
                                                                        </span>
                                                                    </td>
                                                                    <td className="col-conditions">
                                                                        <span className={`mcp-badge ${content.requiredContent ? "mcp-badge--required" : "mcp-badge--optional"}`}>
                                                                            {content.requiredContent ? "OBLIGATOIRE" : "OPTIONNEL"}
                                                                        </span>
                                                                    </td>
                                                                    <td className="col-actions">
                                                                        <button type="button" className="mcp-icon-btn" title="Détails" onClick={() => openDetailPanel(content)}>
                                                                            <Eye size={15} />
                                                                        </button>
                                                                        <button type="button" className="mcp-icon-btn" title="Déblocage" onClick={() => openUnlockPanel(content)}>
                                                                            <Settings2 size={15} />
                                                                        </button>
                                                                        <button type="button" className="mcp-icon-btn" title="Modifier" onClick={() => openEditContent(content.id)}>
                                                                            <Pencil size={15} />
                                                                        </button>
                                                                        <button type="button" className="mcp-icon-btn mcp-icon-btn--danger" title="Supprimer" onClick={() => handleDeleteContent(content.id, content.title)}>
                                                                            <Trash2 size={15} />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                )}
                                                <div className="mcp-add-content-row">
                                                    <button className="mcp-add-content-btn" onClick={() => openCreateContent(section.id)}>
                                                        <Plus size={14} />
                                                        Ajouter un contenu
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </article>
                                );
                            })}
                        </div>
                    )}
                </div>

                <ModuleCleContentFormPanel
                    open={contentPanelOpen}
                    mode={contentPanelMode}
                    moduleCleId={moduleCleId}
                    sections={displaySections}
                    initialSectionId={initialSectionId}
                    onClose={closeAllSidePanels}
                    evaluations={evaluations}
                    quizzes={quizzes}
                    events={events}
                    certificats={certificats}
                />

                <ModuleCleSectionFormPanel
                    open={sectionPanelOpen}
                    mode={sectionPanelMode}
                    moduleCleId={moduleCleId}
                    section={sectionTarget}
                    onClose={closeAllSidePanels}
                />

                <ModuleCleContentUnlockPanel
                    open={unlockPanelOpen}
                    moduleCleId={moduleCleId}
                    content={unlockTarget}
                    contents={displayContents}
                    onClose={closeAllSidePanels}
                />

                <ModuleCleContentDetailsPanel
                    open={detailPanelOpen}
                    content={detailTarget}
                    onClose={closeAllSidePanels}
                />
            </main>
        </div>
    );
}
